import { useMemo, useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';
import Search from '@decisiv/iconix/lib/components/Search';

import Button from '@decisiv/ui-components/lib/components/Button';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import useSort from 'utils/useSort';
import * as ManagementLayout from 'components/ManagementLayout';
import PaginationFooter from 'components/PaginationFooter';
import Loading from 'components/Loading';
import EmptyResults from 'components/EmptyResults';
import SearchableFilter from 'components/SearchableFilter';
import useCreateDepotVisibility from 'hooks/useModalVisibility';
import { NoWrapHeaderTable } from '../components/styles';
import { useManagedDepotsQuery } from './hooks/useManagedDepotsQuery';

import useOemSegmentsFilters from '../OemSegments/hooks/useOemSegmentsFilters';
import useTeamsFilters from '../Teams/hooks/useTeamsFilters';
import useAccountsFilters from '../Fleets/hooks/useAccountsFilters';
import { useManagementPageContext } from '../contexts/ManagementPageContext';
import useDepotsFilters from './hooks/useDepotsFilters';
import { generateColumns } from './columns';

import CreateDepotModal from './CreateDepotModal';

export type ManagedDepotsType = ExtractFromArray<
  ManagedDepotsQuery['managedAssetGroupings']['collection']
>;

const Depots = () => {
  const {
    pageParams,
    handleChangeOemSegmentsFilter,
    handleChangeTeamsFilter,
    handleChangeAccountsFilter,
    handleChangeDepotsFilter,
  } = useManagementPageContext();

  const {
    visible: createDepotModalVisible,
    openModal: openCreateDepotModal,
    closeModal: closeCreateDepotModal,
  } = useCreateDepotVisibility();

  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data, loading } = useManagedDepotsQuery({
    variables: {
      sort,
      page,
      ids: pageParams.depotIds,
      platformAccountIds: pageParams.accountIds,
      sentinelTeamIds: pageParams.teamIds,
      oemSegmentsIds: pageParams.oemSegmentIds,
    },
  });

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleChangeOemSegmentsFilter,
  });

  const teamFilterProps = useTeamsFilters({
    value: pageParams.teamIds,
    oemSegmentIds: pageParams.oemSegmentIds,
    onSubmit: handleChangeTeamsFilter,
  });

  const accountsFilters = useAccountsFilters({
    value: pageParams.accountIds,
    oemSegmentsIds: pageParams.oemSegmentIds,
    sentinelTeamsIds: pageParams.teamIds,
    onSubmit: handleChangeAccountsFilter,
  });

  const depotsFilters = useDepotsFilters({
    value: pageParams.depotIds,
    oemSegmentsIds: pageParams.oemSegmentIds,
    sentinelTeamsIds: pageParams.teamIds,
    platformAccountIds: pageParams.accountIds,
    onSubmit: handleChangeDepotsFilter,
  });

  const columns = useMemo(() => generateColumns({ onSort }), [onSort]);

  const tableData = data?.managedAssetGroupings.collection ?? [];
  const pagination = data?.managedAssetGroupings.pagination;

  return (
    <ManagementLayout.TabBody.Container>
      <ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Header.Left>
          <Button
            text={t`Create Depot`}
            icon={Plus}
            size="small"
            onClick={
              /* istanbul ignore next -- @preserve */ () =>
                openCreateDepotModal()
            }
          />
        </ManagementLayout.TabBody.Header.Left>
        <ManagementLayout.TabBody.Header.Right>
          <P size="small" marginRight={1}>
            <Trans>Search Depots by Name:</Trans>
          </P>
          <SearchInput value="" hideLabel name="searchByDepot" size="small" />
        </ManagementLayout.TabBody.Header.Right>
      </ManagementLayout.TabBody.Header.Container>
      <ManagementLayout.TabBody.Content.Container>
        <ManagementLayout.TabBody.Content.Filters>
          <SearchableFilter
            popoverLabel={t`OEM Segments`}
            placement="bottom-start"
            {...oemSegmentsFilterProps}
          />
          <SearchableFilter popoverLabel={t`Teams`} {...teamFilterProps} />
          <SearchableFilter popoverLabel={t`Accounts`} {...accountsFilters} />
          <SearchableFilter popoverLabel={t`Depots`} {...depotsFilters} />
        </ManagementLayout.TabBody.Content.Filters>
        {loading && <Loading />}
        {!loading && tableData.length === 0 && (
          <EmptyResults
            title={t`No Depots Found`}
            subtitle={t`No Managed Depots Found`}
            icon={Search}
          />
        )}
        {!loading && tableData.length > 0 && (
          <NoWrapHeaderTable
            height="100%"
            scroll={['x', 'y']}
            data={tableData}
            columns={columns}
            onSort={onSort}
            defaultSort={defaultSort}
            footer={() => (
              <PaginationFooter
                // @ts-expect-error - JS file
                title={t`Depots`}
                setCurrentPage={setPage}
                totalOnPage={tableData.length}
                page={pagination?.page}
                perPage={pagination?.perPage}
                totalEntries={pagination?.totalEntries}
                totalPages={pagination?.totalPages}
              />
            )}
          />
        )}
      </ManagementLayout.TabBody.Content.Container>
      {createDepotModalVisible && (
        /* istanbul ignore next -- @preserve */
        <CreateDepotModal
          visible={createDepotModalVisible}
          handleClose={closeCreateDepotModal}
        />
      )}
    </ManagementLayout.TabBody.Container>
  );
};

export default Depots;
